import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, ArrowLeft, Users, Download, Wrench, Briefcase } from 'lucide-react';
import { projects } from '../data/projects';
import { SEO } from '../components/SEO';

type FilterType = 'all' | 'tools' | 'portfolio';

export const AllProjects: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');

  const filteredProjects = useMemo(() => {
    switch (activeFilter) {
      case 'tools':
        return projects.filter(p => !p.isPortfolio);
      case 'portfolio':
        return projects.filter(p => p.isPortfolio);
      default:
        return projects;
    }
  }, [activeFilter]);

  const toolsCount = projects.filter(p => !p.isPortfolio).length;
  const portfolioCount = projects.filter(p => p.isPortfolio).length;

  const isExternalUrl = (url: string) => url.startsWith('http');

  return (
    <div className="pt-24 pb-20">
      <SEO 
        title="All Projects"
        description="Complete portfolio of tools and client work by Hüseyin Cem Ateş. Browser extensions, SaaS products, and web design projects."
        canonical="/portfolio"
      />
      <div className="max-w-6xl mx-auto px-6">
        {/* Back Link */}
        <Link 
          to="/about" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to About
        </Link>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-semibold mb-4">All Projects</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            A complete collection of tools I've built for the community and websites I've designed for clients.
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-muted/50 rounded-lg p-1">
            <button
              onClick={() => setActiveFilter('all')}
              className={`px-5 py-2.5 rounded-md text-sm font-medium transition-all ${
                activeFilter === 'all'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              All Projects ({projects.length})
            </button>
            <button
              onClick={() => setActiveFilter('tools')}
              className={`px-5 py-2.5 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                activeFilter === 'tools'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Wrench className="w-4 h-4" />
              Free Tools ({toolsCount})
            </button>
            <button
              onClick={() => setActiveFilter('portfolio')}
              className={`px-5 py-2.5 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                activeFilter === 'portfolio'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Briefcase className="w-4 h-4" />
              Client Work ({portfolioCount})
            </button>
          </div>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => {
            const isExternal = isExternalUrl(project.url);

            const cardContent = (
              <>
                {/* Image */}
                <div className="aspect-video overflow-hidden bg-muted relative">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  {/* Type Badge */}
                  <div className="absolute top-3 left-3">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                      project.isPortfolio 
                        ? 'bg-foreground/90 text-background' 
                        : 'bg-background/90 text-foreground border border-border'
                    }`}>
                      {project.isPortfolio ? 'Client Work' : 'Free Tool'}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-semibold text-lg group-hover:text-foreground transition-colors line-clamp-1">
                      {project.title}
                    </h3>
                    <ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-1" />
                  </div>
                  
                  <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                    {project.description}
                  </p>

                  {/* Stats (if available) */}
                  {project.stats && (
                    <div className="flex gap-4 mb-4 text-sm">
                      {project.stats.users && (
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Users className="w-3.5 h-3.5" />
                          <span>{project.stats.users}+ users</span>
                        </div>
                      )}
                      {project.stats.downloads && (
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Download className="w-3.5 h-3.5" />
                          <span>{project.stats.downloads}+ downloads</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tags */}
                  <div className="flex flex-wrap gap-1.5">
                    {project.tags?.slice(0, 3).map((tag) => (
                      <span 
                        key={tag} 
                        className="text-xs px-2 py-0.5 bg-muted rounded text-muted-foreground"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </>
            );

            if (isExternal) {
              return (
                <a
                  key={project.id}
                  href={project.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block bg-card border border-border rounded-xl overflow-hidden hover:border-foreground/20 hover:shadow-lg transition-all duration-300"
                >
                  {cardContent}
                </a>
              );
            }

            return (
              <Link
                key={project.id}
                to={project.url}
                className="group block bg-card border border-border rounded-xl overflow-hidden hover:border-foreground/20 hover:shadow-lg transition-all duration-300"
              >
                {cardContent}
              </Link>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <div className="inline-block p-8 bg-muted/30 rounded-2xl">
            <h3 className="text-xl font-semibold mb-2">Have a Project in Mind?</h3>
            <p className="text-muted-foreground mb-6 max-w-md">
              Let's discuss how I can help bring your idea to life.
            </p>
            <Link
              to="/consultation"
              className="inline-flex items-center gap-2 px-6 py-3 bg-foreground text-background rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              Book a Consultation
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
